import { type Dispatch, type SetStateAction } from 'react'

import { DashboardSettingsService } from '@api'
import { zodResolver } from '@hookform/resolvers/zod'
import type { Row } from '@tanstack/react-table'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useQuery } from 'react-query'
import * as z from 'zod'

import stayUndercoverIconLogo from '@/assets/images/stayundercover-icon-logo.png'
import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { TextField } from '@/components/ui/text-field.tsx'
import type { Creator } from '@/modules/creators/types.ts'

interface StayUndercoverDialogProps {
  showStayUndercoverDialog: boolean
  setShowStayUndercoverDialog: Dispatch<SetStateAction<boolean>>
  row: Row<Creator>
}

export type StayUndercoverFormValues = z.infer<typeof StayUndercoverFormSchema>

const StayUndercoverFormSchema = z.object({
  domain: z
    .string()
    .refine(
      (val) =>
        val.split('.').length === 2 &&
        !val.split('.').some((split) => split.length === 0),
    ),
})

const formValues: StayUndercoverFormValues = {
  domain: '',
}

export const StayUndercoverDialog = ({
  row,
  showStayUndercoverDialog,
  setShowStayUndercoverDialog,
}: StayUndercoverDialogProps) => {
  const creator = row.original
  const {
    data: settings,
    isFetching,
    isIdle,
  } = useQuery(
    // Will be fetched every time activeCreator changes
    ['settings', creator.id],
    () =>
      DashboardSettingsService.getDashboardSettings({
        creatorId: creator.id,
      }),
    {
      enabled: showStayUndercoverDialog,
    },
  )
  const form = useForm<StayUndercoverFormValues>({
    mode: 'onSubmit',
    defaultValues: formValues,
    resolver: zodResolver(StayUndercoverFormSchema),
  })

  const closeDialog = () => {
    setShowStayUndercoverDialog(false)
  }

  const onSubmit = (values: StayUndercoverFormValues) => {
    setShowStayUndercoverDialog(false)

    if (!settings) {
      return
    }

    const urlParams = new URLSearchParams()

    urlParams.append('redirectUrl', window.location.href)
    urlParams.append('domain', values.domain)
    urlParams.append('creatorId', String(creator.id))
    urlParams.append('onlyfansId', String(creator.onlyfansId))

    const url = `${settings.linkUrl}?${urlParams.toString()}`

    window.open(url, '_blank')
  }

  return (
    <Dialog
      show={showStayUndercoverDialog}
      onClose={closeDialog}
    >
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Dialog.Header>
            <Dialog.Title className="text-center">
              <div className="inline-flex items-center gap-1.5">
                <img
                  src={stayUndercoverIconLogo}
                  className="inline w-[30px]"
                />
                StayUndercover
              </div>
            </Dialog.Title>
            <Dialog.Description className="text-center">
              <span className="text-title">Redirect</span> your domains &{' '}
              <span className="text-title">hide</span> them from selected
              countries to ensure maximum security. With this feature, you can{' '}
              <span className="text-title">automatically synchronize</span> the
              domain&#39;s click count with CreatorHero.
              <br />
              <a
                href="https://stayundercover.com"
                className="text-primary-400 hover:text-primary-600"
                target="_blank"
                rel="noreferrer"
              >
                Click here to learn more
              </a>
            </Dialog.Description>
          </Dialog.Header>
          <Dialog.Description>
            <div className="my-6 flex flex-col gap-6">
              <div className="flex flex-col gap-1.5">
                <span className="text-title text-sm font-medium">Domain</span>
                <Controller
                  name="domain"
                  control={form.control}
                  render={({ field }) => (
                    <TextField
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Enter Domain"
                    />
                  )}
                />
              </div>
            </div>
          </Dialog.Description>
          <Dialog.ClosableFooter onClose={closeDialog}>
            <Button
              variant="default"
              disabled={
                !form.formState.isValid || !settings || isFetching || isIdle
              }
              type="submit"
              className="w-full"
            >
              Connect
            </Button>
          </Dialog.ClosableFooter>
        </form>
      </FormProvider>
    </Dialog>
  )
}
